/**
 * LOCALIZED_QUESTIONS — spoken prompts for the guided voice intake, keyed by
 * GUIDED_STEPS `id` (see guidedSteps.js), then by BCP-47 language code (the eight
 * locales in languages.js). The on-screen question stays English; only the
 * text read aloud by TTS is localized here.
 *
 * NOTE: The non-English strings are machine-authored translations and should be
 * reviewed by a native speaker before release. Technical terms that are
 * commonly spoken in English in Indian conversation (e.g. "blood group",
 * "pin code", "country code") are intentionally left transliterated/loaned.
 */
export const LOCALIZED_QUESTIONS = {
  firstName: {
    'en-IN': 'Please tell first name',
    'hi-IN': 'कृपया पहला नाम बताएं',
    'mr-IN': 'कृपया पहिले नाव सांगा',
    'gu-IN': 'કૃપા કરીને પહેલું નામ જણાવો',
    'ta-IN': 'முதல் பெயரைச் சொல்லுங்கள்',
    'te-IN': 'దయచేసి మొదటి పేరు చెప్పండి',
    'kn-IN': 'ದಯವಿಟ್ಟು ಮೊದಲ ಹೆಸರು ಹೇಳಿ',
    'bn-IN': 'অনুগ্রহ করে প্রথম নাম বলুন',
  },
  middleName: {
    'en-IN': 'Please tell middle name',
    'hi-IN': 'कृपया मध्य नाम बताएं',
    'mr-IN': 'कृपया मधले नाव सांगा',
    'gu-IN': 'કૃપા કરીને વચ્ચેનું નામ જણાવો',
    'ta-IN': 'நடுப் பெயரைச் சொல்லுங்கள்',
    'te-IN': 'దయచేసి మధ్య పేరు చెప్పండి',
    'kn-IN': 'ದಯವಿಟ್ಟು ಮಧ್ಯದ ಹೆಸರು ಹೇಳಿ',
    'bn-IN': 'অনুগ্রহ করে মধ্য নাম বলুন',
  },
  lastName: {
    'en-IN': 'Please tell last name',
    'hi-IN': 'कृपया अंतिम नाम बताएं',
    'mr-IN': 'कृपया आडनाव सांगा',
    'gu-IN': 'કૃપા કરીને છેલ્લું નામ જણાવો',
    'ta-IN': 'கடைசி பெயரைச் சொல்லுங்கள்',
    'te-IN': 'దయచేసి చివరి పేరు చెప్పండి',
    'kn-IN': 'ದಯವಿಟ್ಟು ಕೊನೆಯ ಹೆಸರು ಹೇಳಿ',
    'bn-IN': 'অনুগ্রহ করে শেষ নাম বলুন',
  },
  email: {
    'en-IN': 'Tell email ID',
    'hi-IN': 'ईमेल आईडी बताएं',
    'mr-IN': 'ईमेल आयडी सांगा',
    'gu-IN': 'ઈમેલ આઈડી જણાવો',
    'ta-IN': 'மின்னஞ்சல் முகவரியைச் சொல்லுங்கள்',
    'te-IN': 'ఇమెయిల్ ఐడీ చెప్పండి',
    'kn-IN': 'ಇಮೇಲ್ ಐಡಿ ಹೇಳಿ',
    'bn-IN': 'ইমেল আইডি বলুন',
  },
  dob: {
    'en-IN': "Please tell patient's date of birth",
    'hi-IN': 'कृपया मरीज़ की जन्म तिथि बताएं',
    'mr-IN': 'कृपया रुग्णाची जन्मतारीख सांगा',
    'gu-IN': 'કૃપા કરીને દર્દીની જન્મ તારીખ જણાવો',
    'ta-IN': 'நோயாளியின் பிறந்த தேதியைச் சொல்லுங்கள்',
    'te-IN': 'దయచేసి రోగి పుట్టిన తేదీ చెప్పండి',
    'kn-IN': 'ದಯವಿಟ್ಟು ರೋಗಿಯ ಜನ್ಮ ದಿನಾಂಕ ಹೇಳಿ',
    'bn-IN': 'অনুগ্রহ করে রোগীর জন্ম তারিখ বলুন',
  },
  gender: {
    'en-IN': "patient's gender",
    'hi-IN': 'मरीज़ का लिंग',
    'mr-IN': 'रुग्णाचे लिंग',
    'gu-IN': 'દર્દીનું લિંગ',
    'ta-IN': 'நோயாளியின் பாலினம்',
    'te-IN': 'రోగి లింగం',
    'kn-IN': 'ರೋಗಿಯ ಲಿಂಗ',
    'bn-IN': 'রোগীর লিঙ্গ',
  },
  bloodGroup: {
    'en-IN': 'Tell blood group of patient',
    'hi-IN': 'मरीज़ का रक्त समूह बताएं',
    'mr-IN': 'रुग्णाचा रक्तगट सांगा',
    'gu-IN': 'દર્દીનું રક્ત જૂથ જણાવો',
    'ta-IN': 'நோயாளியின் இரத்தப் பிரிவைச் சொல்லுங்கள்',
    'te-IN': 'రోగి రక్త వర్గం చెప్పండి',
    'kn-IN': 'ರೋಗಿಯ ರಕ್ತದ ಗುಂಪು ಹೇಳಿ',
    'bn-IN': 'রোগীর রক্তের গ্রুপ বলুন',
  },
  religion: {
    'en-IN': 'Tell religion',
    'hi-IN': 'धर्म बताएं',
    'mr-IN': 'धर्म सांगा',
    'gu-IN': 'ધર્મ જણાવો',
    'ta-IN': 'மதத்தைச் சொல்லுங்கள்',
    'te-IN': 'మతం చెప్పండి',
    'kn-IN': 'ಧರ್ಮ ಹೇಳಿ',
    'bn-IN': 'ধর্ম বলুন',
  },
  education: {
    'en-IN': "Tell patient's educational qualification",
    'hi-IN': 'मरीज़ की शैक्षणिक योग्यता बताएं',
    'mr-IN': 'रुग्णाची शैक्षणिक पात्रता सांगा',
    'gu-IN': 'દર્દીની શૈક્ષણિક લાયકાત જણાવો',
    'ta-IN': 'நோயாளியின் கல்வித் தகுதியைச் சொல்லுங்கள்',
    'te-IN': 'రోగి విద్యార్హత చెప్పండి',
    'kn-IN': 'ರೋಗಿಯ ಶೈಕ್ಷಣಿಕ ಅರ್ಹತೆ ಹೇಳಿ',
    'bn-IN': 'রোগীর শিক্ষাগত যোগ্যতা বলুন',
  },
  occupation: {
    'en-IN': "Tell patient's occupation",
    'hi-IN': 'मरीज़ का व्यवसाय बताएं',
    'mr-IN': 'रुग्णाचा व्यवसाय सांगा',
    'gu-IN': 'દર્દીનો વ્યવસાય જણાવો',
    'ta-IN': 'நோயாளியின் தொழிலைச் சொல்லுங்கள்',
    'te-IN': 'రోగి వృత్తి చెప్పండి',
    'kn-IN': 'ರೋಗಿಯ ವೃತ್ತಿ ಹೇಳಿ',
    'bn-IN': 'রোগীর পেশা বলুন',
  },
  maritalStatus: {
    'en-IN': 'Is the patient married?',
    'hi-IN': 'क्या मरीज़ विवाहित है?',
    'mr-IN': 'रुग्ण विवाहित आहे का?',
    'gu-IN': 'શું દર્દી પરિણીત છે?',
    'ta-IN': 'நோயாளி திருமணமானவரா?',
    'te-IN': 'రోగి వివాహితుడా?',
    'kn-IN': 'ರೋಗಿ ವಿವಾಹಿತರೇ?',
    'bn-IN': 'রোগী কি বিবাহিত?',
  },
  idProofType: {
    'en-IN': 'Which ID proof is submitted?',
    'hi-IN': 'कौन सा पहचान प्रमाण जमा किया गया है?',
    'mr-IN': 'कोणता ओळखपत्र सादर केला आहे?',
    'gu-IN': 'કયો ઓળખ પુરાવો સબમિટ કરેલ છે?',
    'ta-IN': 'எந்த அடையாளச் சான்று சமர்ப்பிக்கப்பட்டது?',
    'te-IN': 'ఏ గుర్తింపు రుజువు సమర్పించారు?',
    'kn-IN': 'ಯಾವ ಗುರುತಿನ ಪುರಾವೆ ಸಲ್ಲಿಸಲಾಗಿದೆ?',
    'bn-IN': 'কোন পরিচয় প্রমাণ জমা দেওয়া হয়েছে?',
  },
  idProofNumber: {
    'en-IN': 'Share ID proof number',
    'hi-IN': 'पहचान प्रमाण संख्या बताएं',
    'mr-IN': 'ओळखपत्र क्रमांक सांगा',
    'gu-IN': 'ઓળખ પુરાવા નંબર જણાવો',
    'ta-IN': 'அடையாளச் சான்று எண்ணைச் சொல்லுங்கள்',
    'te-IN': 'గుర్తింపు రుజువు సంఖ్య చెప్పండి',
    'kn-IN': 'ಗುರುತಿನ ಪುರಾವೆ ಸಂಖ್ಯೆ ಹೇಳಿ',
    'bn-IN': 'পরিচয় প্রমাণ নম্বর বলুন',
  },
  emergencyContactPerson: {
    'en-IN': 'Tell name of the relative or friend?',
    'hi-IN': 'रिश्तेदार या मित्र का नाम बताएं?',
    'mr-IN': 'नातेवाईक किंवा मित्राचे नाव सांगा?',
    'gu-IN': 'સંબંધી કે મિત્રનું નામ જણાવો?',
    'ta-IN': 'உறவினர் அல்லது நண்பரின் பெயரைச் சொல்லுங்கள்?',
    'te-IN': 'బంధువు లేదా స్నేహితుడి పేరు చెప్పండి?',
    'kn-IN': 'ಸಂಬಂಧಿ ಅಥವಾ ಸ್ನೇಹಿತರ ಹೆಸರು ಹೇಳಿ?',
    'bn-IN': 'আত্মীয় বা বন্ধুর নাম বলুন?',
  },
  emergencyContactRelation: {
    'en-IN': 'Relative relation with the patient?',
    'hi-IN': 'मरीज़ के साथ रिश्तेदार का संबंध?',
    'mr-IN': 'रुग्णाशी नातेवाईकाचे नाते?',
    'gu-IN': 'દર્દી સાથે સંબંધીનો સંબંધ?',
    'ta-IN': 'நோயாளியுடன் உறவினரின் உறவு?',
    'te-IN': 'రోగితో బంధువు సంబంధం?',
    'kn-IN': 'ರೋಗಿಯೊಂದಿಗೆ ಸಂಬಂಧಿಯ ಸಂಬಂಧ?',
    'bn-IN': 'রোগীর সাথে আত্মীয়ের সম্পর্ক?',
  },
  emergencyMobileNumber: {
    'en-IN': "Relative or friend's contact number?",
    'hi-IN': 'रिश्तेदार या मित्र का संपर्क नंबर?',
    'mr-IN': 'नातेवाईक किंवा मित्राचा संपर्क क्रमांक?',
    'gu-IN': 'સંબંધી કે મિત્રનો સંપર્ક નંબર?',
    'ta-IN': 'உறவினர் அல்லது நண்பரின் தொடர்பு எண்?',
    'te-IN': 'బంధువు లేదా స్నేహితుడి సంప్రదింపు నంబర్?',
    'kn-IN': 'ಸಂಬಂಧಿ ಅಥವಾ ಸ್ನೇಹಿತರ ಸಂಪರ್ಕ ಸಂಖ್ಯೆ?',
    'bn-IN': 'আত্মীয় বা বন্ধুর যোগাযোগ নম্বর?',
  },
  phoneNumber1: {
    'en-IN': "What is patient's number?",
    'hi-IN': 'मरीज़ का नंबर क्या है?',
    'mr-IN': 'रुग्णाचा क्रमांक काय आहे?',
    'gu-IN': 'દર્દીનો નંબર શું છે?',
    'ta-IN': 'நோயாளியின் எண் என்ன?',
    'te-IN': 'రోగి నంబర్ ఏమిటి?',
    'kn-IN': 'ರೋಗಿಯ ಸಂಖ್ಯೆ ಏನು?',
    'bn-IN': 'রোগীর নম্বর কী?',
  },
  addressline1: {
    'en-IN': "Tell patient's address - line 1",
    'hi-IN': 'मरीज़ का पता बताएं - पंक्ति 1',
    'mr-IN': 'रुग्णाचा पत्ता सांगा - ओळ 1',
    'gu-IN': 'દર્દીનું સરનામું જણાવો - લાઇન 1',
    'ta-IN': 'நோயாளியின் முகவரியைச் சொல்லுங்கள் - வரி 1',
    'te-IN': 'రోగి చిరునామా చెప్పండి - లైన్ 1',
    'kn-IN': 'ರೋಗಿಯ ವಿಳಾಸ ಹೇಳಿ - ಸಾಲು 1',
    'bn-IN': 'রোগীর ঠিকানা বলুন - লাইন 1',
  },
  addressline2: {
    'en-IN': "Tell patient's address - line 2",
    'hi-IN': 'मरीज़ का पता बताएं - पंक्ति 2',
    'mr-IN': 'रुग्णाचा पत्ता सांगा - ओळ 2',
    'gu-IN': 'દર્દીનું સરનામું જણાવો - લાઇન 2',
    'ta-IN': 'நோயாளியின் முகவரியைச் சொல்லுங்கள் - வரி 2',
    'te-IN': 'రోగి చిరునామా చెప్పండి - లైన్ 2',
    'kn-IN': 'ರೋಗಿಯ ವಿಳಾಸ ಹೇಳಿ - ಸಾಲು 2',
    'bn-IN': 'রোগীর ঠিকানা বলুন - লাইন 2',
  },
  country: {
    'en-IN': 'Country of residence?',
    'hi-IN': 'निवास का देश?',
    'mr-IN': 'राहण्याचा देश?',
    'gu-IN': 'રહેઠાણનો દેશ?',
    'ta-IN': 'வசிக்கும் நாடு?',
    'te-IN': 'నివాస దేశం?',
    'kn-IN': 'ವಾಸದ ದೇಶ?',
    'bn-IN': 'বসবাসের দেশ?',
  },
  state: {
    'en-IN': 'state or province?',
    'hi-IN': 'राज्य या प्रांत?',
    'mr-IN': 'राज्य किंवा प्रांत?',
    'gu-IN': 'રાજ્ય કે પ્રાંત?',
    'ta-IN': 'மாநிலம் அல்லது மாகாணம்?',
    'te-IN': 'రాష్ట్రం లేదా ప్రావిన్స్?',
    'kn-IN': 'ರಾಜ್ಯ ಅಥವಾ ಪ್ರಾಂತ್ಯ?',
    'bn-IN': 'রাজ্য বা প্রদেশ?',
  },
  city: {
    'en-IN': 'city or village name?',
    'hi-IN': 'शहर या गाँव का नाम?',
    'mr-IN': 'शहर किंवा गावाचे नाव?',
    'gu-IN': 'શહેર કે ગામનું નામ?',
    'ta-IN': 'நகரம் அல்லது கிராமத்தின் பெயர்?',
    'te-IN': 'నగరం లేదా గ్రామం పేరు?',
    'kn-IN': 'ನಗರ ಅಥವಾ ಗ್ರಾಮದ ಹೆಸರು?',
    'bn-IN': 'শহর বা গ্রামের নাম?',
  },
  pinCode: {
    'en-IN': 'pincode',
    'hi-IN': 'पिन कोड',
    'mr-IN': 'पिन कोड',
    'gu-IN': 'પિન કોડ',
    'ta-IN': 'பின் கோடு',
    'te-IN': 'పిన్ కోడ్',
    'kn-IN': 'ಪಿನ್ ಕೋಡ್',
    'bn-IN': 'পিন কোড',
  },
  phoneCountryCode: {
    'en-IN': 'Tell country code',
    'hi-IN': 'देश कोड बताएं',
    'mr-IN': 'देश कोड सांगा',
    'gu-IN': 'દેશ કોડ જણાવો',
    'ta-IN': 'நாட்டுக் குறியீட்டைச் சொல்லுங்கள்',
    'te-IN': 'దేశ కోడ్ చెప్పండి',
    'kn-IN': 'ದೇಶದ ಕೋಡ್ ಹೇಳಿ',
    'bn-IN': 'দেশের কোড বলুন',
  },
  primaryConsultant: {
    'en-IN': 'Who is the primary consultant in hospital?',
    'hi-IN': 'अस्पताल में प्राथमिक सलाहकार कौन है?',
    'mr-IN': 'रुग्णालयातील प्राथमिक सल्लागार कोण आहे?',
    'gu-IN': 'હોસ્પિટલમાં પ્રાથમિક સલાહકાર કોણ છે?',
    'ta-IN': 'மருத்துவமனையில் முதன்மை ஆலோசகர் யார்?',
    'te-IN': 'ఆసుపత్రిలో ప్రధాన సలహాదారు ఎవరు?',
    'kn-IN': 'ಆಸ್ಪತ್ರೆಯಲ್ಲಿ ಪ್ರಾಥಮಿಕ ಸಲಹೆಗಾರರು ಯಾರು?',
    'bn-IN': 'হাসপাতালে প্রধান পরামর্শদাতা কে?',
  },
  institute: {
    'en-IN': 'Tell hospital or center name.',
    'hi-IN': 'अस्पताल या केंद्र का नाम बताएं।',
    'mr-IN': 'रुग्णालय किंवा केंद्राचे नाव सांगा.',
    'gu-IN': 'હોસ્પિટલ કે કેન્દ્રનું નામ જણાવો.',
    'ta-IN': 'மருத்துவமனை அல்லது மையத்தின் பெயரைச் சொல்லுங்கள்.',
    'te-IN': 'ఆసుపత్రి లేదా కేంద్రం పేరు చెప్పండి.',
    'kn-IN': 'ಆಸ್ಪತ್ರೆ ಅಥವಾ ಕೇಂದ್ರದ ಹೆಸರು ಹೇಳಿ.',
    'bn-IN': 'হাসপাতাল বা কেন্দ্রের নাম বলুন।',
  },
  patientType: {
    'en-IN': 'Tell if outpatient or inpatient',
    'hi-IN': 'बताएं कि बाह्य रोगी है या आंतरिक रोगी',
    'mr-IN': 'बाह्यरुग्ण की आंतररुग्ण ते सांगा',
    'gu-IN': 'બહારના દર્દી કે અંદરના દર્દી તે જણાવો',
    'ta-IN': 'வெளிநோயாளியா அல்லது உள்நோயாளியா எனச் சொல்லுங்கள்',
    'te-IN': 'బాహ్య రోగినా లేదా అంతర్ రోగినా చెప్పండి',
    'kn-IN': 'ಹೊರರೋಗಿಯೇ ಅಥವಾ ಒಳರೋಗಿಯೇ ಎಂದು ಹೇಳಿ',
    'bn-IN': 'বহির্বিভাগের রোগী নাকি আন্তঃবিভাগের রোগী তা বলুন',
  },
};

/**
 * Returns the spoken prompt for a step in the requested language, falling back
 * to English, then to a generic sentence built from the step id.
 */
export function getLocalizedQuestion(stepId, lang) {
  const dict = LOCALIZED_QUESTIONS[stepId];
  if (dict) {
    return dict[lang] || dict['en-IN'];
  }
  return `What is the ${stepId}?`;
}
